import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import defer from 'lodash/defer';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { setLocationPath } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { FOLDER_NAME, CONFIG_FILE_NAME } from './constants';
import { fetchS3JsonFile, scrollTo } from '../AmexMarketingUtils/AmexUtils';
import AmexHeader from '../AmexMarketingUtils/AmexHeader';
import AmexSecurityCenterKeepAccountSafe from './AmexSecurityCenterKeepAccountSafe';
import AmexSecurityCenterProtectScams from './AmexSecurityCenterProtectScams';
import AmexBanner from '../AmexMarketingUtils/AmexBanner';
import AmexSecurityCenterHeader from './AmexSecurityCenterHeader';
import AmexFAQs from '../AmexMarketingUtils/AmexFAQs';
import AmexSecurityCenterBrokerActivity from './AmexSecurityCenterBrokerActivity';
import AmexLoading from '../AmexMarketingUtils/AmexLoading';
import './AmexSecurityCenter.scss';

class AmexSecurityCenter extends React.Component {
  static propTypes = {
    $location: PropTypes.shape({
      path: PropTypes.func.isRequired,
      host: PropTypes.func.isRequired,
      search: PropTypes.func.isRequired,
    }).isRequired,
    $rootScope: PropTypes.shape({
      $apply: PropTypes.func.isRequired,
      $digest: PropTypes.func.isRequired,
      matchMedia: PropTypes.shape({
        xs: PropTypes.bool,
        sm: PropTypes.bool,
        md: PropTypes.bool,
        lg: PropTypes.bool,
        ipadLandscape: PropTypes.bool,
        xl: PropTypes.bool,
      }).isRequired,
      metadata: PropTypes.shape({
        image_twitter: PropTypes.string.isRequired,
        image_facebook: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    $routeParams: PropTypes.shape({}).isRequired,
    Analytics: PropTypes.shape({
      event: PropTypes.func,
      marketingCustomPage: PropTypes.func,
    }).isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isNonUS: PropTypes.bool.isRequired,
    userLanguage: PropTypes.string,
    ResyAmexConfig: PropTypes.shape({
      baseRequestUrl: PropTypes.string.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    userLanguage: navigator.languages ? navigator.languages[0] : navigator.language,
  }

  state = {
    contentData: {},
  }

  componentDidMount() {
    const currentHash = (window.location.hash).replace('#', '');
    window.addEventListener('hashchange', this.handleHashChange, false);
    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
    } = this.props;
    this.fetchDataWithConfig(baseRequestUrl, currentHash);
  }

  async componentDidUpdate(prevProps, prevState) {
    const { hash } = this.state;
    if (prevState.hash !== hash) {
      window.location.hash = hash;
      if (hash) {
        const updatedHash = hash.replace('#', '');
        const elementToScroll = document.getElementById(updatedHash);
        if (!elementToScroll) return;

        setTimeout(() => {
          window.scrollTo({
            top: elementToScroll.offsetTop - 120,
            behavior: 'smooth',
          });
        }, 1000);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.handleHashChange, false);
  }

  updateHash = (hash) => this.setState({ hash });

  fetchDataFile = async (languagesPermitted, baseRequestUrl, currentHash) => {
    const {
      userLanguage,
    } = this.props;

    let fileName;
    const langArr = languagesPermitted.split(',');

    const match = langArr.find((element) => element === userLanguage);

    if (match) {
      fileName = `SecurityCenter_${userLanguage}.json`;
    } else {
      fileName = 'SecurityCenter_en-US.json';
    }
    const contentData = await fetchS3JsonFile(baseRequestUrl, FOLDER_NAME, fileName);
    this.setState({ contentData, hash: currentHash });
    this.renderPagePreviewImage(baseRequestUrl, contentData);
  }

  fetchDataWithConfig = async (baseRequestUrl, currentHash) => {
    const configResponse = await fetchS3JsonFile(baseRequestUrl, FOLDER_NAME, CONFIG_FILE_NAME);
    if (configResponse) {
      const {
        languages_permitted: languagesPermitted = 'en-US',
      } = configResponse;
      if (languagesPermitted) this.fetchDataFile(languagesPermitted, baseRequestUrl, currentHash);
    }
  }

  handleHashChange = () => {
    const currentLocationHash = (window.location.hash).replace('#', '');
    if (currentLocationHash && currentLocationHash !== '') {
      this.setState({ hash: currentLocationHash });
    }
  };

  renderPagePreviewImage = (baseRequestUrl, contentData) => {
    const {
      $rootScope,
    } = this.props;
    const {
      preview_image: previewImage = '',
    } = contentData;

    const urlPrefix = `${baseRequestUrl}SecurityCenter/images/`;
    const defaultPreviewImage = `${baseRequestUrl}SecurityCenter/bannerImages/banner_mobile_regular.jpg`;
    const customPreviewImage = previewImage || defaultPreviewImage;

    $rootScope.metadata.image_twitter = `${urlPrefix}${customPreviewImage}`;
    $rootScope.metadata.image_facebook = `${urlPrefix}${customPreviewImage}`;
  }

  render() {
    const {
      $location,
      $rootScope,
      $rootScope: {
        matchMedia,
      },
      $routeParams,
      Analytics,
      isAuthenticated,
      isNonUS,
      ResyAmexConfig: {
        baseRequestUrl,
      },
    } = this.props;

    const {
      contentData,
      hash,
    } = this.state;

    if (Object.keys(contentData).length === 0) return <AmexLoading />;

    const {
      aws_folder_name: awsFolderName,
      is_page_enabled: isPageEnabled = false,
      banner: bannerConfig,
      header: headerConfig,
      keep_account_safe: keepAccountSafeConfig,
      faqs: faqsConfig,
      resy_header: resyHeaderConfig,
      broker_activity: brokerActivityConfig,
      stop_bot: stopBotConfig,
      protect_against_scams: scamsConfig,
    } = contentData;

    if (!isPageEnabled) {
      // 404 error page
      setLocationPath('/404', () => defer(() => $rootScope.$apply()));
      defer(() => $rootScope.$digest());
      return null;
    }

    const hideKeepAccountSafe = keepAccountSafeConfig?.hide_section;
    const hideFAQs = faqsConfig?.hide_section;
    const hideProtectScams = scamsConfig?.hide_section;
    const hideStopBotBrokerActivity = brokerActivityConfig?.hide_section;

    const displayMenuItems = {
      account: !hideKeepAccountSafe,
      faqs: !hideFAQs,
      protect: !hideProtectScams,
      stopBot: !hideStopBotBrokerActivity,
    };

    return (
      <section className="AmexSecurityCenter">
        <AmexHeader
          Analytics={Analytics}
          isAuthenticated={isAuthenticated}
          isNonUSLocation={isNonUS}
          $rootScope={$rootScope}
          $routeParams={$routeParams}
          $location={$location}
          matchMedia={matchMedia}
          resyHeaderConfig={resyHeaderConfig}
        />
        <AmexSecurityCenterHeader
          baseRequestUrl={baseRequestUrl}
          headerConfig={headerConfig}
          displayMenuItems={displayMenuItems}
          matchMedia={matchMedia}
          scrollTo={scrollTo}
          eventAnalytics={Analytics.event}
        />
        <AmexBanner
          awsFolderName={awsFolderName}
          baseRequestUrl={baseRequestUrl}
          bannerConfig={bannerConfig}
          matchMedia={matchMedia}
        />
        {!hideKeepAccountSafe && (
          <AmexSecurityCenterKeepAccountSafe
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            keepAccountSafeConfig={keepAccountSafeConfig}
            matchMedia={matchMedia}
            hash={hash}
            updateHash={this.updateHash}
          />
        )}
        {!hideProtectScams && (
          <AmexSecurityCenterProtectScams
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            scamsConfig={scamsConfig}
            matchMedia={matchMedia}
            hash={hash}
          />
        )}
        {!hideStopBotBrokerActivity && (
          <AmexSecurityCenterBrokerActivity
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            matchMedia={matchMedia}
            brokerActivityConfig={brokerActivityConfig}
            expandableConfig={stopBotConfig}
            eventAnalytics={Analytics.event}
            updateHash={this.updateHash}
            hash={hash}
          />
        )}
        {!hideFAQs && (
          <AmexFAQs
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            faqsConfig={faqsConfig}
            eventAnalytics={Analytics.event}
            isSectionScroll
            matchMedia={matchMedia}
            hash={hash}
          />
        )}
      </section>
    );
  }
}

const mapStateToProps = (state) => ({
  isAuthenticated: selectIsAuthenticated(state),
  isNonUS: state.ipdata?.isNonUS,
});

AmexSecurityCenter.displayName = 'AmexSecurityCenter';
export { AmexSecurityCenter as ComponentAmexSecurityCenterUnderTest, mapStateToProps };
export default connect(mapStateToProps)(AmexSecurityCenter);
